import { Icon, InputLabel } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import { ChangeEvent, useId } from "react";

import { ClickableWrapper } from "../ClickableWrapper";
import { ResponsiveSpinner } from "../ResponsiveSpinner";
import { SearchInputProps } from "./types";

export const SearchInput = ({
  label,
  name,
  placeholder,
  autoFocus = false,
  disabled = false,
  onChange,
  onBlur,
  onFocus,
  value = "",
  className,
  inputClassName = "",
  error = false,
  loading = false,
  ...otherProps
}: SearchInputProps) => {
  const inputId = useId();

  const inputClsx = clsx(
    // The [&:-webkit-autofill] rule stops Chrome/Safari's autofill from
    // painting its own (white) background over ours once a value has been
    // remembered for this field -- an absurdly long transition-delay is the
    // standard trick, since the browser won't wait that long to apply it.
    "typo-body-sm block h-10 w-full truncate rounded-lg bg-surface-tonal-default p-4 pe-10 ps-10 text-[rgb(26,27,27)] outline transition-colors placeholder:text-text-subdued hover:bg-overlay-opacity-state-hover [&:-webkit-autofill]:[transition:background-color_600000s_0s,color_600000s_0s]",
    // Error takes over the outline (default/hover/focus) only -- icon
    // colour, font style, surface colour, and text colour above are
    // untouched either way. Error is 2px in every state, not just focus.
    error
      ? "outline-2 outline-stroke-error hover:outline-stroke-error focus:outline-stroke-error"
      : "outline-1 outline-border-primary-medium hover:outline-border-blue-base focus:outline-2 focus:outline-stroke-focused",
    inputClassName,
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(e.target.value);
  };

  const handleClear = () => {
    if (onChange) onChange("");
  };

  return (
    <div className={`flex w-full min-w-40 flex-col gap-y-2 ${className}`}>
      {label && <InputLabel value={label} htmlFor={inputId} />}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <Icon
            icon="search"
            size={16}
            responsiveClasses="!text-icon-tertiary"
          />
        </div>
        <input
          id={inputId}
          name={name}
          type="text"
          className={inputClsx}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={error}
          {...otherProps}
        />
        {loading ? (
          <div className="absolute inset-y-0 end-3 flex items-center">
            <ResponsiveSpinner width={16} height={16} />
          </div>
        ) : (
          value.length > 0 && (
            <div className="absolute inset-y-0 end-3 flex items-center">
              <ClickableWrapper onClick={handleClear}>
                <Icon icon="cross" size="tiny" />
              </ClickableWrapper>
            </div>
          )
        )}
      </div>
    </div>
  );
};
